import React, {useState} from "react";
import {Alert, Button, Card, Form, InputLabel, TextField} from "peggirkit";
import {EmployeeDashboardProps} from "@widgets/timeRegistration/EmployeeDashboard";
import {FormattedMessage, useIntl} from "react-intl";
import {EmployeeSignInForm, employeeSignInFormValidation, EmployeeStatus} from "@/data/entities/employee";
import {useForm} from "react-hook-form";
import {useMutation} from "@tanstack/react-query";
import {getInputValidationProps} from "@/data/Validation";
import {AxiosError} from "axios";
import {api} from "@/data/api";

type Props = EmployeeDashboardProps & {
    wrapInCard: boolean,
};

const EmployeeLogin = ({setEmployee, wrapInCard}: Props) => {
    const intl = useIntl();
    const [loading, setLoading] = useState(false);
    const [errorMsgId, setErrorMsgId] = useState<string | null>(null);
    const {register, handleSubmit, formState: {errors}} = useForm<EmployeeSignInForm>();
    const {mutate: signIn} = useMutation({
        mutationFn: (form: EmployeeSignInForm) => api.employee.getEmployeeStatus(form.code),
        onSuccess: (authenticatedUser: EmployeeStatus) => setEmployee(authenticatedUser),
        onError: (e: AxiosError) => {
            if (e.status === 404) {
                // Invalid code
                setErrorMsgId("invalidEmployeeCode");
                return;
            }

            // HTTP 403 Not allowed (IP-whitelist)
            setErrorMsgId("employeeUnableToSignIn");
        },
        onSettled: () => setLoading(false),
    });
    const handleSubmitHandler: (() => void) = handleSubmit((data: EmployeeSignInForm) => {
        setLoading(true);
        setErrorMsgId(null);
        signIn(data);
    });

    const form = (
        <Form
            onSubmit={handleSubmitHandler}
            alert={
                errorMsgId !== null
                    ? <Alert
                        type={"danger"}
                        title={intl.formatMessage({id: errorMsgId})}
                    />
                    : undefined
            }
        >
            <div className={"w-full flex flex-col gap-6"}>
                <TextField
                    type={"text"}
                    autoComplete={"off"}
                    maxLength={4}
                    label={
                        <InputLabel>
                            <FormattedMessage id={"employeeCode"}/>
                        </InputLabel>
                    }
                    placeholder={"0000"}
                    tip={<FormattedMessage id={"employeeLoginCodeTip"}/>}
                    disabled={loading}
                    error={errors.code && errors.code.message}
                    {...register("code", {
                        ...getInputValidationProps(employeeSignInFormValidation.code, intl)
                    })}
                />
            </div>

            <Button
                width={"w-full"}
                buttonAction={{
                    as: "button",
                    actionProps: {
                        onClick: e => {
                            e.preventDefault();
                            handleSubmitHandler();
                        },
                    },
                }}
                align={"center"}
                loading={loading}
            >
                <FormattedMessage id={"signIn"}/>
            </Button>
        </Form>
    );

    if (!wrapInCard)
        return <>{form}</>;

    return <Card>{form}</Card>;
};

export default EmployeeLogin;